import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	CircularProgress,
	FormHelperText,
	TextField,
	FormLabel,
	Stack,
	Box,
	Grid,
} from '@mui/material';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import {
	engagementsApi,
	useCreateNoteMutation,
	useGeneratePdfMutation,
} from '@/features/engagements/engagementsApi';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '@/utils/firebase';
import { formatDate, getUrlFromPipeline } from '@/utils/constants';
import {
	STORAGE_PURPOSES,
	clientStoragePath,
	dealStoragePath,
	relationshipCompanyStoragePath,
} from '@/utils/storagePaths';
import FileUpload from '@/components/FileUpload';
import { dealsApi } from '@/features/deals/dealsApi';
import { triggerRefresh } from '@/features/table/attachmentsTableSlice';
import { useObjectContext } from '@/hooks/useObjectContext';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { ticketsApi } from '@/features/claims/ticketsApi';
import { useAddFileToSharepointMutation } from '@/features/msGraph/msGraphApi';
import { useParams } from 'react-router-dom';
import { paths } from '@/app/routes';
import { hideToast, showToast } from '@/features/toast/toastSlice';
import { FOLDER_NAME_MAX } from '@/components/dialogs/AddFolderSharepointDialog/validationSchema';
import FormikNoteObject from '@/components/forms/fields/FormikNoteObject';
import { useMsGraph } from '@/hooks/useMsGraph';
import TiptapEditor from '@/components/TiptapEditor';
import NoteFolderSelect from './NoteFolderSelect';
import { AssociatedClientDealSelect } from '@/components/selects/AssociatedClientDealSelect';

const hasSpecialChars = (value) => /[<>:"'/\\|?%*]/.test(value);

const isClientNote = (values) => Boolean(values?.client && !values?.deal);

const noteObjectType = ({ isRelationshipCompany, isClient, deal }) => {
	if (isRelationshipCompany) {
		return 'relationshipCompany';
	}
	if (isClient) {
		return 'client';
	}
	return deal?.objectType ?? 'deal';
};

// const SUBJECT_MAX = 20;
const validationSchema = Yup.object().shape({
	body: Yup.string().required('Note is required.'),
	subject: Yup.string()
		.max(FOLDER_NAME_MAX, `Subject should be no more than ${FOLDER_NAME_MAX} characters`)
		.test('no-special-characters', 'Name contains invalid characters.', (value) => {
			if (!value || value.trim() === '') return true;
			return !hasSpecialChars(value);
		}),
	id: Yup.string().required('Note must be associated with a client or deal'),
});

const NoteDataWrapper = () => {
	const params = useParams();

	const { clientRow, dealRow, relationshipCompany } = useObjectContext();

	const resolvedClientId = clientRow?.clientId || params.clientId;
	const isRelationshipCompany = Boolean(params.relationshipCompanyId);
	const dealId = isRelationshipCompany ? null : dealRow?.dealId || params.dealId;

	const dialogData = useSelector((state) => state.dialog.data?.note);

	const initialValues = {
		id:
			dialogData?.id ??
			(isRelationshipCompany ? params.relationshipCompanyId : (dealId ?? resolvedClientId)),
		html: dialogData?.html ?? '',
		body: '',
		subject: dialogData?.subject ?? '',
		files: [],
		tags: [],
		errorMessage: '',
		...(isRelationshipCompany
			? {
					client: null,
					deal: null,
					relationshipCompany,
					driveId: relationshipCompany?.notesDriveId ?? relationshipCompany?.driveId,
					clientFolderDriveId: relationshipCompany?.clientFolderDriveId,
				}
			: {
					client: dialogData?.client ?? (resolvedClientId ? clientRow : null),
					deal: dialogData?.deal ?? (dealId ? dealRow : null),
					relationshipCompany: null,
					driveId: dealRow?.notesDriveId ?? clientRow?.notesDriveId,
					clientFolderDriveId:
						dealRow?.clientFolderDriveId ?? clientRow?.clientFolderDriveId,
				}),
	};

	const dispatch = useDispatch();
	const account = useSelector((state) => state.msalAccount.account);

	const { uploadFilesAndRetry } = useMsGraph();

	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const [createNote, { isLoading }] = useCreateNoteMutation();

	const [addFileToSharepoint] = useAddFileToSharepointMutation();
	const [generatePdfMutation] = useGeneratePdfMutation();

	const handleClose = () => {
		dispatch(closeDialog('note'));
	};

	const handleCreateNote = async (values) => {
		const isClient = isClientNote(values);
		const objectType = noteObjectType({
			isRelationshipCompany,
			isClient,
			deal: values.deal,
		});
		const subject = (values.subject ?? '').toString().trim();

		const finalSubject =
			!subject || subject.length === 0
				? `Created Note - ${formatDate(new Date(), true).replace(':', '.')}`
				: subject;

		const selectedDrive = values.selectedDrive;

		console.log('[handleCreateNote] Using noteDriveId:', selectedDrive?.id);

		const noteBody = `${values.subject ? `<p>${values?.subject?.trim()}:</p>` : ''}${values.html}`;

		const body = {
			clientId: isRelationshipCompany ? null : values.client?.clientId,
			dealId: isRelationshipCompany ? null : values.deal?.dealId,
			relationshipCompanyId: isRelationshipCompany ? params.relationshipCompanyId : null,
			noteBody,
			objectType,
			subject: finalSubject,
			driveId: selectedDrive?.id,
			clientFolderDriveId: selectedDrive?.parentReference?.driveId,
			parentFolderDriveId: selectedDrive?.parentReference?.id,
			folderName: selectedDrive?.name,
			folderPath: values.folderPath,
		};

		console.log('📋 ~ Create note object', body);

		//If tenant hasn't given us consent generate pdf front end
		try {
			if (!userDetails?.tenantConsented && selectedDrive) {
				const { blobUrl } = await generatePdfMutation({
					htmlContent: noteBody,
					fileName: finalSubject,
				}).unwrap();
				const blob = await fetch(blobUrl).then((r) => r.blob());
				const file = new File([blob], `${finalSubject}.pdf`, { type: 'application/pdf' });

				const fileRes = await addFileToSharepoint({
					driveId: selectedDrive.parentReference?.driveId,
					itemId: selectedDrive.id,
					file,
				}).unwrap();
				body.fileId = fileRes?.id;
				body.fileName = fileRes?.name;
			}
		} catch (e) {
			console.error(`There was an error uploading note PDF ${finalSubject}`, e);
		}

		const response = await createNote(body).unwrap();
		console.log('Response', response);

		const uploadFilesRes = await uploadFilesToStorage({
			noteId: response.noteId,
			driveId: selectedDrive?.id,
			clientFolderDriveId: selectedDrive?.parentReference?.driveId,
			values,
			objectType,
		});
		console.log('Upload files res', uploadFilesRes);
		return response;
	};

	const uploadFilesToStorage = async ({
		noteId,
		driveId,
		clientFolderDriveId,
		values,
		objectType,
	}) => {
		const isClient = isClientNote(values);
		const type = objectType.charAt(0).toUpperCase() + objectType.slice(1);

		const getFilePath = (fileName) => {
			if (isRelationshipCompany) {
				return relationshipCompanyStoragePath(
					values.id,
					STORAGE_PURPOSES.attachments,
					fileName,
				);
			}
			if (isClient) {
				return clientStoragePath(values.client.id, STORAGE_PURPOSES.attachments, fileName);
			}
			return dealStoragePath(
				{ clientDocId: values.deal.clientDocId, dealDocId: values.deal.id },
				STORAGE_PURPOSES.attachments,
				fileName,
			);
		};

		console.log('[uploadFilesToStorage] driveId:', driveId);

		if (driveId && values.files.length > 0) {
			await uploadFilesAndRetry({
				driveId: clientFolderDriveId,
				itemId: driveId,
				files: values.files,
			});
		}

		await Promise.all(
			values.files.map(async (file) => {
				const storageRef = ref(storage, getFilePath(file.name));
				const metadata = {
					customMetadata: {
						[`${type} ID`]: values.id.toString(),
						'Note ID': noteId.toString(),
					},
				};
				await uploadBytes(storageRef, file, metadata);
			}),
		).then((r) => {
			console.log(`Uploaded ${values.files.length} files`, values.files, r);
		});

		setTimeout(async () => {
			try {
				if (!isRelationshipCompany) {
					const uppercaseType = (
						isClient ? 'client' : (values?.deal?.objectType ?? '')
					).toUpperCase();
					const objectApi = values?.deal?.objectType == 'deal' ? dealsApi : ticketsApi;
					dispatch(
						objectApi.util.invalidateTags([
							{ type: `${uppercaseType}S`, id: 'LIST' },
							{
								type: `${uppercaseType}S`,
								id: values?.deal?.dealStage,
							},
							{ type: uppercaseType, id: values.id },
						]),
					);
					dispatch(dealsApi.util.invalidateTags([{ type: 'DEAL_ROW', id: values.id }]));
				}
				dispatch(
					engagementsApi.util.invalidateTags([
						{ type: 'ENGAGEMENTS', id: values.id },
						{ type: 'TASKS', id: 'LIST' },
					]),
				);
				dispatch(triggerRefresh());
			} catch (error) {
				console.error('Error invalidating API:', error);
			}
		}, 2000);
	};

	return (
		<Formik
			{...{
				initialValues,
				validateOnChange: false,
				validateOnBlur: true,
				validationSchema,
				enableReinitialize: true,
				onSubmit: async (values, { resetForm }) => {
					console.log('📋 Form submitted', values);
					handleClose();
					resetForm();

					// because toasts stake now need to store the id so I an remove it from array... set Id
					const toastId = Math.random();
					dispatch(
						showToast({
							message: 'Creating note...',
							autohide: false,
							loading: true,
							id: toastId,
						}),
					);
					try {
						const res = await handleCreateNote(values);
						dispatch(hideToast(toastId));
						const isClient = isClientNote(values);
						if (res?.error) {
							dispatch(
								showToast({
									message: `Error creating note! ${res?.error?.message}`,
									severity: 'error',
								}),
							);
						} else {
							let notePath = `${getUrlFromPipeline(values?.deal?.pipeline)}/${values?.deal?.dealId}/notes`;
							if (isRelationshipCompany) {
								notePath = `${paths.companies}/${params.relationshipCompanyId}/notes`;
							} else if (isClient) {
								notePath = `${paths.clients}/${values?.client?.clientId ?? ''}/notes`;
							}
							dispatch(
								showToast({
									message: 'Note created!',
									autohide: true,
									action: {
										path: notePath,
										label: 'GO TO NOTE',
										params: { state: { tab: 'notes' } },
									},
								}),
							);
						}
					} catch (err) {
						console.error('🙅 ~ Error creating note', err);
						dispatch(
							showToast({
								message: 'Error creating note. Please try again.',
								severity: 'error',
							}),
						);
					}
				},
			}}
		>
			{({
				isSubmitting,
				setFieldValue,
				validateField,
				values,
				handleSubmit,
				handleChange,
				touched,
				errors,
				getFieldProps,
				resetForm,
			}) => (
				<Form>
					<Dialog
						open={true}
						onClose={(_, reason) => {
							// Prevent closing on Escape key or backdrop click
							if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
								return;
							}
							// Only allow closing via the Cancel button logic
							resetForm();
							handleClose();
						}}
						hideBackdrop={true}
						PaperProps={{
							sx: {
								minWidth: { xs: '100%', sm: '70%', md: '50%' },
							},
						}}
					>
						<DialogTitle>Create Note</DialogTitle>
						<DialogContent sx={{ paddingBottom: '2em' }}>
							<FormikNoteObject />
							<Grid container spacing={1}>
								{isRelationshipCompany ? (
									<Grid size={12}>
										<FormLabel>Company</FormLabel>
										<TextField
											fullWidth
											value={relationshipCompany?.name ?? ''}
											slotProps={{ input: { readOnly: true } }}
										/>
									</Grid>
								) : (
									<AssociatedClientDealSelect
										gridSize={{ xs: 12, sm: 6 }}
										clientDisabled={
											isSubmitting ||
											Boolean(params?.clientId) ||
											Boolean(params?.dealId)
										}
										dealDisabled={
											isSubmitting || !values.client || Boolean(params?.dealId)
										}
										onClientChange={(client) => {
											console.log('Selected client', client);
											setFieldValue('selectedFolder', '');
											setFieldValue('selectedDrive', null);
											setFieldValue('folderPath', '');
											setFieldValue('client', client);
											setFieldValue('id', client?.clientId);
											setFieldValue('deal', null);
										}}
										onDealChange={(newDeal) => {
											console.log('Selected deal', newDeal);
											setFieldValue('selectedFolder', '');
											setFieldValue('selectedDrive', null);
											setFieldValue('folderPath', '');
											setFieldValue('deal', newDeal);
											setFieldValue('id', newDeal?.dealId);
										}}
									/>
								)}

								<Grid size={{ xs: 12, sm: 6 }}>
									<FormLabel>{'Subject'}</FormLabel>
									<TextField
										fullWidth={true}
										name='subject'
										placeholder={`Subject (max ${FOLDER_NAME_MAX} characters)`}
										{...getFieldProps('subject')}
										error={touched.subject && Boolean(errors.subject)}
										disabled={isSubmitting}
									/>
									<Stack direction='row' justifyContent='space-between'>
										<FormHelperText
											error={touched.subject && Boolean(errors.subject)}
										>
											{touched.subject && errors.subject}
										</FormHelperText>

										<FormHelperText
											error={
												(touched.subject && Boolean(errors.subject)) ||
												values.subject.length > FOLDER_NAME_MAX
											}
											sx={{
												...(values.subject.length >= FOLDER_NAME_MAX && {
													fontWeight: 'bold',
												}),
												mr: '14px',
											}}
										>
											<span>{values.subject.length}</span>
											{`/${FOLDER_NAME_MAX}`}
										</FormHelperText>
									</Stack>
								</Grid>
								<Grid size={{ xs: 12, sm: 6 }}>
									<NoteFolderSelect />
								</Grid>
								<Grid size={12}>
									<FileUpload
										files={values.files}
										setFiles={(files) =>
											handleChange({
												target: {
													name: 'files',
													value: files,
												},
											})
										}
										loading={isSubmitting}
									/>
								</Grid>
								<Grid size={12}>
									<Box
										sx={(theme) => ({
											'& .tiptap-editor': {
												// ...(theme.palette.mode === 'dark' && {
												// 	backgroundColor:
												// 		theme.components?.MuiInputBase
												// 			?.styleOverrides?.root?.backgroundColor,
												// }),
												borderColor:
													touched.body && Boolean(errors.body)
														? 'error.main'
														: // : theme.palette.mode === 'dark'
															// 	? theme.components?.MuiInputBase
															// 			?.styleOverrides?.root
															// 			?.borderColor
															theme.palette.divider,
											},
										})}
									>
										<TiptapEditor
											value={values.html}
											minHeight='50vh' 
											onChange={(html, content) => {
												setFieldValue('html', html);
												setFieldValue('body', content.trim());
											}}
											onBlur={async () => {
												await validateField('body');
											}}
											error={touched.body && Boolean(errors.body)}
											disabled={isSubmitting}
											placeholder='Note details here...'
											config={{
												toolbar: {
													user: {
														show: false,
														showSignature: false,
													},
													brokerage: { show: false },
													image: { show: false },
													table: { show: false },
													deal: { show: false },
													client: { show: false },
													riskInfo: { show: false },
													align: { show: true },
												},
											}}
										/>
									</Box>

									{/* Error helper text */}
									{touched.body && errors.body && (
										<FormHelperText error>{errors.body}</FormHelperText>
									)}
								</Grid>
							</Grid>
							{values.errorMessage.length > 0 && (
								<FormHelperText error>{values.errorMessage}</FormHelperText>
							)}
						</DialogContent>
						<DialogActions>
							<Button
								onClick={(e) => {
									e.preventDefault();
									resetForm();
									handleClose();
								}}
								color='primary'
							>
								Cancel
							</Button>
							<Button
								color='primary'
								variant='contained'
								disabled={isLoading || isSubmitting}
								onClick={(e) => {
									handleSubmit();
								}}
							>
								{isSubmitting ? (
									<CircularProgress size='2em' sx={{ color: '#ffffff' }} />
								) : (
									'Create'
								)}
							</Button>
						</DialogActions>
					</Dialog>
				</Form>
			)}
		</Formik>
	);
};

export const AddNoteDialog = () => {
	const open = useSelector((state) => state.dialog.open?.note);

	if (!open) return <></>;
	return <NoteDataWrapper />;
};
